import React, { useState, useEffect, useRef } from 'react';
// Inline SVG components to match the vendor form's design
const CloseIcon = ({ size = 24, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);
const SaveIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
    <polyline points="17 21 17 13 7 13 7 21"></polyline>
    <polyline points="7 3 7 8 15 8"></polyline>
  </svg>
);
const SpinnerIcon = ({ size = 16, className = '' }) => (
  <svg className={`animate-spin ${className}`} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="2" x2="12" y2="6"></line>
    <line x1="12" y1="18" x2="12" y2="22"></line>
    <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
    <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
    <line x1="2" y1="12" x2="6" y2="12"></line>
    <line x1="18" y1="12" x2="22" y2="12"></line>
    <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
    <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
  </svg>
);
const ReceiptIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7"></path>
    <path d="M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4"></path>
    <path d="M4 17c0 2.21 3.582 4 8 4s8-1.79 8-4"></path>
  </svg>
);
// New Maximize Icon
const MaximizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);
// New Minimize Icon
const MinimizeIcon = ({ size = 20, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);
const API_URL = '/api';
const initialFormData = {
  tax_code: '',
  tax_type: '',
  tax_name: '',
  description: '',
  rate: '0.00',
  country: '',
  is_default: false,
  status: 'Active',
  department_id: '' // Added department_id field
};
const TaxFormModal = ({ open, onClose, onSubmit, initialData, isEdit, loading }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoadingCode, setIsLoadingCode] = useState(false);
  const [activeTab, setActiveTab] = useState('taxInfo');
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [isUserValid, setIsUserValid] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [isMaximized, setIsMaximized] = useState(false); // New state for maximize/minimize
  
  // Department-related state
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [screenConfig, setScreenConfig] = useState(null);
  const [showDepartmentField, setShowDepartmentField] = useState(false);
  
  // Refs for potential future file inputs
  const fileInputRefs = useRef({});
  
  const tabs = [
    { id: 'taxInfo', name: 'Tax Info' },
    { id: 'description', name: 'Description' },
  ];
  
  // Function to fetch current user
  const fetchCurrentUser = async () => {
    try {
    const token = localStorage.getItem('authToken'); // store token after login
    if (!token) {
      setIsUserValid(false);
      setAuthError('No token found. Please log in again.');
      return false;
    }
    const userRes = await fetch(`${API_URL}/current-user`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    const userData = await userRes.json();
      
      if (userData.user && userData.user.UserID) {
        setCurrentUser(userData.user);
        setIsUserValid(true);
        setAuthError(null);
        console.log('Current user fetched:', userData);
        return true;
      } else {
        console.error('Invalid user data received:', userData);
        setIsUserValid(false);
        setAuthError('User information not available. Please log in again.');
        return false;
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
      setIsUserValid(false);
      setAuthError('Error fetching user information. Please refresh the page.');
      return false;
    }
  };
  
  // Function to fetch departments
  const fetchDepartments = async () => {
    setLoadingDepartments(true);
    try {
      const response = await fetch(`${API_URL}/departments`);
      const data = await response.json();
      console.log('Fetched departments:', data);
      setDepartments(data);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };
  
  // Function to fetch screen configuration
  const fetchScreenConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/screens`);
      const data = await response.json();
      console.log('Fetched screen configurations:', data);
      
      // Find the tax screen configuration
      const taxScreen = data.screens.find(screen => 
        screen.ScreenName.toLowerCase() === 'TaxMaster'.toLowerCase()
      );
      
      console.log('Tax screen configuration:', taxScreen);
      if (taxScreen) {
        setScreenConfig(taxScreen);
        // Show department field if ShowOnHeader is 1
        const shouldShowDepartment = taxScreen.ShowOnHeader === true;
        setShowDepartmentField(shouldShowDepartment);
        
        // If department field should be shown, fetch departments
        if (shouldShowDepartment) {
          fetchDepartments();
        }
      }
    } catch (error) {
      console.error('Error fetching screen configuration:', error);
      setShowDepartmentField(false);
    }
  };
  
  // Toggle maximize/minimize
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };
  
  useEffect(() => {
    fetchCurrentUser();
  }, []);
  
  useEffect(() => {
    if (open) {
      // Fetch current user when modal opens
      fetchCurrentUser();
      
      // Fetch screen configuration to determine if department field should be shown
      fetchScreenConfig();
      
      if (isEdit && initialData) {
        // Create a copy of initialData without created_by and updated_by
        const { CreatedBy, UpdatedBy, ...dataWithoutUserFields } = initialData;
        
        setFormData({
          tax_code: dataWithoutUserFields.TaxCode || '',
          tax_type: dataWithoutUserFields.TaxType || '',
          tax_name: dataWithoutUserFields.TaxName || '',
          description: dataWithoutUserFields.Description || '',
          rate: dataWithoutUserFields.Rate?.toString() || '0.00',
          country: dataWithoutUserFields.Country || '',
          is_default: dataWithoutUserFields.IsDefault === true || dataWithoutUserFields.IsDefault === 1,
          status: (dataWithoutUserFields.Status === true || dataWithoutUserFields.Status === 1) ? 'Active' : 'Inactive',
          department_id: dataWithoutUserFields.DepartmentID || '' // Added department_id
        });
      } else {
        // Only fetch new code if creating and code is empty or default
        if (!formData.tax_code || formData.tax_code === initialFormData.tax_code) {
          fetchNextCode();
        }
        setFormData(prev => ({
          ...prev,
          is_default: false,
          status: 'Active',
          department_id: '' // Reset department_id for new entries
        }));
      }
      setCurrentStep(0);
      setActiveTab('taxInfo');
    } else {
      // Reset department-related state when modal closes
      setShowDepartmentField(false);
      setScreenConfig(null);
      setDepartments([]);
    }
  }, [open, initialData, isEdit]);
  
  const fetchNextCode = async () => {
    setIsLoadingCode(true);
    try {
      const response = await fetch(`${API_URL}/taxlatestcode`);
      const data = await response.json();
      setFormData(prev => ({ ...prev, tax_code: data.nextCode }));
    } catch (error) {
      console.error('Error fetching next tax code:', error);
    } finally {
      setIsLoadingCode(false);
    }
  };
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const goToNextStep = () => {
    if (currentStep < tabs.length - 1) {
      setCurrentStep(prev => prev + 1);
      setActiveTab(tabs[currentStep + 1].id);
    }
  };
  
  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      setActiveTab(tabs[currentStep - 1].id);
    }
  };
  
  const saveCurrentStep = async () => {
    setFormLoading(true);
    
    try {
      // Simulate saving - in a real app, this would be an API call
      console.log(`Saving step ${currentStep + 1}: ${tabs[currentStep].name}`);
      
      // If this is the last step, submit the entire form
      if (currentStep === tabs.length - 1) {
        await handleSubmit();
      } else {
        // Move to next step
        goToNextStep();
      }
    } catch (error) {
      console.error('Error saving step:', error);
      alert('An error occurred while saving. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (isSubmitting || formLoading || loading) {
      console.log('Form submission already in progress, ignoring duplicate submission');
      return;
    }
    
    // Check if we have a valid current user
    if (!isUserValid || !currentUser || !currentUser.UserID) {
      alert(authError || 'User information not available. Please refresh the page and log in again.');
      return;
    }
    
    setIsSubmitting(true);
    setFormLoading(true);
    
    const dataToSend = {
      ...formData,
      status: formData.status === 'Active' ? 1 : 0,
      is_default: formData.is_default ? 1 : 0,
    };
    
    // Only append the relevant user ID field based on operation
    const userId = parseInt(currentUser.UserID);
    console.log('Using user ID:', userId);
    
    if (isEdit && initialData) {
      dataToSend.updated_by = userId;
      console.log('Appended updated_by:', userId);
    } else {
      dataToSend.created_by = userId;
      console.log('Appended created_by:', userId);
    }
    
    // Include department_id only if showDepartmentField is true
    if (showDepartmentField) {
      dataToSend.department_id = formData.department_id;
    }
    
    // Log data for debugging
    console.log('Submitting data:', dataToSend);
    
    try {
      await onSubmit(dataToSend);
      onClose();
    } catch (error) {
      console.error('Error saving tax:', error);
      alert(error.message || 'An error occurred while saving the tax');
    } finally {
      setFormLoading(false);
      setIsSubmitting(false);
    }
  };
  if (!open) return null;
  
  return (
    <div
      className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out
        ${open ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
    >
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none; /* Internet Explorer 10+ */
            scrollbar-width: none; /* Firefox */
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none; /* Safari and Chrome */
          }
          .custom-scrollbar::-webkit-scrollbar {
            width: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        `}
      </style>
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      
      {/* This is the actual slide-in panel with fixed header and scrollable content */}
      <div 
        className={`bg-white shadow-2xl flex flex-col
          transition-all transform-gpu duration-300 ease-in-out
          ${open ? 'translate-x-0' : 'translate-x-full'}
          ${isMaximized ? 'w-full h-full max-w-none rounded-none' : 'w-full max-w-6xl h-full '}`}
      >
        {/* Fixed Header */}
        <div className="flex-shrink-0 p-8 pb-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#6A89A7] rounded-full flex items-center justify-center">
                <ReceiptIcon className="text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#384959]">
                  {isEdit ? 'Edit Tax' : 'Create New Tax'}
                </h2>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Maximize/Minimize Button */}
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1 rounded-full hover:bg-[#F1F5F9]" 
                onClick={toggleMaximize}
                title={isMaximized ? "Minimize" : "Maximize"}
              >
                {isMaximized ? <MinimizeIcon /> : <MaximizeIcon />}
              </button>
              {/* Close Button */}
              <button 
                className="text-[#A0AEC0] hover:text-[#384959] transition-colors p-1 rounded-full hover:bg-[#F1F5F9]" 
                onClick={onClose}
                title="Close"
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        </div>
        
        {/* User validation warning */}
        {open && !isUserValid && (
          <div className="mx-8 mb-4 bg-red-50 border-l-4 border-red-500 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">
                  {authError || 'User information not available. Please refresh the page and log in again.'}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Scrollable Content Area */}
        <div className="flex-grow overflow-y-auto custom-scrollbar px-8">
          <form onSubmit={handleSubmit} className="space-y-6 pb-6">
            {/* Tax Information Section */}
            <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
              <h3 className="text-lg font-semibold text-[#384959] mb-4">Tax Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Tax Code */}
                <div className='flex flex-col gap-2'>
                  <label className="block text-sm font-medium text-[#64748b]">
                    Tax Code *
                  </label>
                  <input
                    type="text"
                    value={isLoadingCode ? 'Loading...' : formData.tax_code}
                    onChange={(e) => handleInputChange('tax_code', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"
                    placeholder="e.g., GST01, VATUK"
                    required
                  />
                </div>
                
                {/* Tax Type */}
                <div className='flex flex-col gap-2'>
                  <label className="block text-sm font-medium text-[#64748b]">
                    Tax Type *
                  </label>
                  <select
                    value={formData.tax_type}
                    onChange={(e) => handleInputChange('tax_type', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                    required
                  >
                    <option value="">Select Tax Type</option>
                    <option value="GST">GST</option>
                    <option value="VAT">VAT</option>
                    <option value="Sales Tax">Sales Tax</option>
                    <option value="Income Tax">Income Tax</option>
                    <option value="Customs Duty">Customs Duty</option>
                    <option value="Excise Duty">Excise Duty</option>
                    <option value="Service Tax">Service Tax</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                {/* Tax Name */}
                <div className='flex flex-col gap-2'>
                  <label className="block text-sm font-medium text-[#64748b]">
                    Tax Name *
                  </label>
                  <input
                    type="text"
                    value={formData.tax_name}
                    onChange={(e) => handleInputChange('tax_name', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                    placeholder="e.g., Goods and Services Tax"
                    required
                  />
                </div>
                
                {/* Rate (%) */}
                <div className='flex flex-col gap-2'>
                  <label className="block text-sm font-medium text-[#64748b]">
                    Rate (%) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.rate}
                    onChange={(e) => handleInputChange('rate', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"
                    placeholder="0.00"
                    min="0"
                    max="100"
                    required
                  />
                </div>
                {/* Department Selection Field - Only shown if ShowOnHeader is 1 */}
                {showDepartmentField && (
                  <div className='flex flex-col gap-2 '>
                    <label className="block text-sm font-medium text-[#64748b]">
                      Department *
                    </label>
                    <select
                      name="department_id"
                      value={formData.department_id}
                      onChange={(e) => handleInputChange('department_id', e.target.value)}
                      className={`w-full rounded-md border border-[#E2E8F0] bg-white px-3 py-2 shadow-sm focus:ring-2 focus:ring-[#BDDDFC] focus:border-[#BDDDFC] transition-colors uppercase`}
                      disabled={loadingDepartments}
                    >
                      <option value="">{loadingDepartments ? 'Loading departments...' : 'Select Department'}</option>
                      {departments.map((dept) => (
                        <option key={dept.DepartmentID} value={dept.DepartmentID}>
                          {dept.DepartmentCode} - {dept.DepartmentName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* Country */}
                <div className='flex flex-col gap-2'>
                  <label className="block text-sm font-medium text-[#64748b]">
                    Country
                  </label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                    placeholder="e.g., India"
                  />
                </div>
                
                {/* Status */}
                <div className='flex flex-col gap-2'>
                  <label className="block text-sm font-medium text-[#64748b]">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
                
                
                
                {/* IsDefault checkbox */}
                <div className="flex items-center gap-2 col-span-2">
                  <input
                    type="checkbox"
                    id="is_default"
                    checked={formData.is_default}
                    onChange={(e) => handleInputChange('is_default', e.target.checked)}
                    className="w-4 h-4 text-[#6A89A7] border-[#E2E8F0] rounded focus:ring-[#BDDDFC]"
                  />
                  <label htmlFor="is_default" className="text-sm font-medium text-[#64748b]">
                    Set as Default Tax
                  </label>
                </div>
              </div>
            </div>
            
            {/* Description Section */}
            <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
              <h3 className="text-lg font-semibold text-[#384959] mb-4">Description</h3>
              <div className='flex flex-col gap-2'>
                <label className="block text-sm font-medium text-[#64748b]">
                  Details
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                  placeholder="Enter tax description..."
                  rows="5"
                />
              </div>
            </div>
          </form>
        </div>
        
        {/* Fixed Footer with Buttons */}
        <div className="flex-shrink-0 p-8 pt-0">
          <div className="flex justify-end gap-3">
            <button 
              type="button" 
              className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]" 
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              type="submit"
              form="tax-form"
              className="flex items-center gap-2 px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 bg-[#6A89A7] hover:bg-[#5a7997] disabled:opacity-50 disabled:cursor-not-allowed" 
              disabled={loading || formLoading || isSubmitting || !isUserValid}
              onClick={handleSubmit}
            >
              {formLoading || isSubmitting || loading ? (
                <>
                  <SpinnerIcon />
                  Saving...
                </>
              ) : (
                <>
                  <SaveIcon />
                  {isEdit ? 'Update' : 'Create'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
export default TaxFormModal;